import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useAbonnements,
  useChangerStatutAbonnement,
  useClients,
  useSouscrire,
} from '../api/queries';
import type { Abonnement, FormuleSms, StatutAbonnement } from '../api/types';
import { Bouton } from '../components/Boutons';
import { Carte } from '../components/Carte';
import { Selecteur } from '../components/Champs';
import { MessageErreur } from '../components/MessageErreur';
import { Tableau, type Colonne } from '../components/Tableau';
import { formaterDate, formaterNombre } from '../lib/format';
import { formaterEuros } from './ClientsPage';

/**
 * Option SMS — abonnements et revenu récurrent.
 *
 * La spec front affiche le canal SMS grisé et non fonctionnel. Le rendre disponible en option
 * payante est une décision commerciale postérieure : le canal existait déjà dans les
 * préférences de notification, il lui manquait un DROIT D'USAGE. C'est ce droit qui se gère ici.
 *
 * Conséquence pratique à garder en tête : couper un abonnement ne modifie pas la préférence de
 * l'utilisateur. Il continue de vouloir des SMS, il n'y a simplement plus droit — et il les
 * retrouvera tels quels si l'abonnement reprend.
 */

const OPTIONS_STATUT = [
  { valeur: 'ACTIF', libelle: 'Actif' },
  { valeur: 'ESSAI', libelle: 'Période d’essai' },
  { valeur: 'IMPAYE', libelle: 'Impayé' },
  { valeur: 'SUSPENDU', libelle: 'Suspendu' },
  { valeur: 'RESILIE', libelle: 'Résilié' },
];

const OPTIONS_FORMULE = [
  { valeur: 'ESSAI', libelle: 'Essai — 20 SMS, gratuit 30 jours' },
  { valeur: 'ESSENTIEL', libelle: 'Essentiel — 50 SMS/mois, 9,90 €' },
  { valeur: 'CONFORT', libelle: 'Confort — 200 SMS/mois, 19,90 €' },
  { valeur: 'ILLIMITE', libelle: 'Illimité — 39,90 €' },
];

const COULEUR_STATUT: Record<StatutAbonnement, string> = {
  ACTIF: '#16733A',
  ESSAI: '#2563EB',
  IMPAYE: '#D0342C',
  SUSPENDU: '#ED7D0E',
  RESILIE: '#6B7480',
};

export function AbonnementsPage(): JSX.Element {
  const [parametres, setParametres] = useSearchParams();
  const statut = parametres.get('statut') ?? '';

  const { data, error, isPending } = useAbonnements(statut ? { statut } : {});
  const { data: clients } = useClients({});

  const souscrire = useSouscrire();
  const changerStatut = useChangerStatutAbonnement();

  const [exploitationId, setExploitationId] = useState('');
  const [formule, setFormule] = useState<FormuleSms>('CONFORT');

  // Ne proposer à la souscription que les clients qui n'ont pas déjà un abonnement vivant :
  // l'unicité est garantie en base, autant ne pas laisser l'opérateur buter dessus.
  const sansAbonnement = (clients?.items ?? []).filter((c) => c.abonnement === null);

  const colonnes: Colonne<Abonnement>[] = [
    {
      cle: 'client',
      entete: 'Client',
      rendu: (a) => (
        <div>
          <div style={{ fontWeight: 600 }}>{a.exploitation_nom ?? a.exploitation_id}</div>
          <div style={{ fontSize: 12, color: '#6B7480' }}>
            depuis le {formaterDate(a.debut_at)}
          </div>
        </div>
      ),
    },
    { cle: 'formule', entete: 'Formule', rendu: (a) => a.formule },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (a) => (
        <span style={{ color: COULEUR_STATUT[a.statut], fontWeight: 600 }}>{a.statut}</span>
      ),
    },
    {
      cle: 'quota',
      entete: 'Consommation du mois',
      rendu: (a) =>
        a.quota_mensuel === null ? (
          <span style={{ color: '#6B7480' }}>{formaterNombre(a.sms_consommes_mois)} SMS · illimité</span>
        ) : (
          <div>
            <div>
              {formaterNombre(a.sms_consommes_mois)} / {formaterNombre(a.quota_mensuel)} SMS
            </div>
            {/* Jauge : un quota dépassé se voit avant qu'un client n'appelle pour le signaler. */}
            <div
              style={{
                height: 5,
                background: '#E3E7EC',
                borderRadius: 99,
                marginTop: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, a.quota_utilise_pct ?? 0)}%`,
                  height: '100%',
                  background: (a.quota_utilise_pct ?? 0) >= 90 ? '#D0342C' : '#16733A',
                }}
              />
            </div>
          </div>
        ),
    },
    {
      cle: 'prix',
      entete: 'Prix / mois',
      numerique: true,
      rendu: (a) => formaterEuros(a.prix_mensuel_cents),
    },
    {
      cle: 'cout',
      entete: 'Coût opérateur',
      numerique: true,
      // Prix payé par le client face au coût réel des envois : la marge de l'option se lit
      // d'un coup d'œil, ligne par ligne.
      rendu: (a) => (
        <span style={{ color: a.cout_operateur_mois_cents > a.prix_mensuel_cents ? '#D0342C' : '#6B7480' }}>
          {formaterEuros(a.cout_operateur_mois_cents)}
        </span>
      ),
    },
    {
      cle: 'actions',
      entete: '',
      rendu: (a) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          {a.statut !== 'RESILIE' ? (
            <>
              {a.statut === 'SUSPENDU' || a.statut === 'IMPAYE' ? (
                <Bouton
                  variante="neutre"
                  petit
                  onClick={() => {
                    changerStatut.mutate({ id: a.id, statut: 'ACTIF', motif: 'Réactivation manuelle' });
                  }}
                >
                  Réactiver
                </Bouton>
              ) : (
                <Bouton
                  variante="neutre"
                  petit
                  onClick={() => {
                    changerStatut.mutate({ id: a.id, statut: 'SUSPENDU', motif: 'Suspension manuelle' });
                  }}
                >
                  Suspendre
                </Bouton>
              )}
              <Bouton
                variante="danger"
                petit
                onClick={() => {
                  if (window.confirm(`Résilier l’abonnement de ${a.exploitation_nom} ?`)) {
                    changerStatut.mutate({ id: a.id, statut: 'RESILIE', motif: 'Résiliation manuelle' });
                  }
                }}
              >
                Résilier
              </Bouton>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <h1 style={{ margin: 0 }}>Option SMS</h1>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <Tuile
          libelle="Revenu récurrent"
          valeur={formaterEuros(data?.revenu_mensuel_cents ?? 0)}
          accent="#16733A"
          aide="Somme des abonnements actifs"
        />
        <Tuile libelle="Abonnements" valeur={formaterNombre(data?.total ?? 0)} />
        <Tuile
          libelle="SMS envoyés ce mois"
          valeur={formaterNombre((data?.items ?? []).reduce((s, a) => s + a.envois_mois, 0))}
        />
        <Tuile
          libelle="Coût opérateur"
          valeur={formaterEuros(
            (data?.items ?? []).reduce((s, a) => s + a.cout_operateur_mois_cents, 0),
          )}
        />
      </div>

      <Carte titre="Souscrire pour un client">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Selecteur
              label="Client"
              optionVide="Choisir un client…"
              options={sansAbonnement.map((c) => ({ valeur: c.id, libelle: c.nom }))}
              value={exploitationId}
              onChange={(e) => {
                setExploitationId(e.currentTarget.value);
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Selecteur
              label="Formule"
              options={OPTIONS_FORMULE}
              value={formule}
              onChange={(e) => {
                setFormule(e.currentTarget.value as FormuleSms);
              }}
            />
          </div>
          <Bouton
            disabled={exploitationId === '' || souscrire.isPending}
            onClick={() => {
              souscrire.mutate(
                { exploitation_id: exploitationId, formule },
                { onSuccess: () => setExploitationId('') },
              );
            }}
          >
            Souscrire
          </Bouton>
        </div>
        {souscrire.error ? <MessageErreur erreur={souscrire.error} /> : null}
        {sansAbonnement.length === 0 ? (
          <p style={{ color: '#6B7480', fontSize: 13, marginTop: 12, marginBottom: 0 }}>
            Tous les clients listés ont déjà un abonnement. Un client ne peut en avoir qu’un seul
            à la fois.
          </p>
        ) : null}
      </Carte>

      <Carte>
        <div style={{ marginBottom: 16, maxWidth: 260 }}>
          <Selecteur
            label="Statut"
            optionVide="Tous les statuts"
            options={OPTIONS_STATUT}
            value={statut}
            onChange={(e) => {
              const suivants = new URLSearchParams(parametres);
              if (e.currentTarget.value === '') suivants.delete('statut');
              else suivants.set('statut', e.currentTarget.value);
              setParametres(suivants, { replace: true });
            }}
          />
        </div>

        {error ? <MessageErreur erreur={error} /> : null}
        {changerStatut.error ? <MessageErreur erreur={changerStatut.error} /> : null}

        <Tableau
          colonnes={colonnes}
          lignes={data?.items ?? []}
          cleDe={(a) => a.id}
          chargement={isPending}
          titreVide="Aucun abonnement"
          descriptionVide="Aucun client n’a souscrit à l’option SMS."
        />
      </Carte>
    </div>
  );
}

function Tuile({
  libelle,
  valeur,
  accent,
  aide,
}: {
  libelle: string;
  valeur: string;
  accent?: string;
  aide?: string;
}): JSX.Element {
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E3E7EC',
        borderRadius: 12,
        padding: '14px 16px',
      }}
    >
      <div style={{ fontSize: 12, color: '#6B7480', textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {libelle}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent ?? '#14181F', marginTop: 4 }}>
        {valeur}
      </div>
      {aide ? <div style={{ fontSize: 11, color: '#9AA3AE', marginTop: 2 }}>{aide}</div> : null}
    </div>
  );
}
